import {
  Box,
  Button,
  Center,
  Group,
  Paper,
  Text,
  useMantineTheme,
} from "@mantine/core";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import Chip from "~/components/Chip";
import DetailDialog from "~/components/DetailDialog";
import MeqTable from "~/components/MeqTable/MeqTable";
import type {
  MeqRow,
  MeqRowData,
  MeqSortingState,
} from "~/components/MeqTable/types";
import Spinner from "~/components/Spinner";
import SvgIconRaw, { type IconName } from "~/components/SvgIconRaw";
import type { LooseObject } from "~/utils/Types";

import FiltersPopover from "./FiltersPopover";
import GridSortControl from "./GridSortControl";
import ImageTileGrid from "./ImageTileGrid";
import ImageViewer from "./ImageViewer";
import {
  applyImageFilters,
  countActiveFilters,
  getColumnDataKey,
  getColumnId,
  getFilterAvailability,
  getSoleActiveFilterLabel,
  hasColumn,
  sortImageRows,
  withTableSorting,
} from "./filtering";
import type {
  ImagesFilterState,
  ImagesModalMode,
  ImagesModalProps,
  ImagesSortState,
} from "./types";
import { DEFAULT_ROW_KEYS, EMPTY_FILTER_STATE } from "./types";

/**
 * One modal over a resolved image set, in either a table or a grid.
 *
 * Row resolution runs filters → (table only) search → sort. Filters and sort
 * are both shared, so switching mode never changes which rows are in play or
 * the order they come in: the grid's sort control and the table's column
 * headers read and write the one sort. Only the search is the table's own.
 * The image viewer is fed the ACTIVE mode's resolved rows, so prev/next walks
 * exactly what was on screen.
 */
const MODES = [
  { icon: "Menu", labelKey: "imagesModal.modeTable", value: "table" },
  { icon: "Image", labelKey: "imagesModal.modeGrid", value: "grid" },
] as const satisfies readonly {
  icon: IconName;
  labelKey: string;
  value: ImagesModalMode;
}[];

const ImagesModal = ({
  ausMarblingKey = DEFAULT_ROW_KEYS.ausMarbling,
  bodyIdKey = DEFAULT_ROW_KEYS.bodyId,
  columns,
  contentTitle,
  dateKey = DEFAULT_ROW_KEYS.date,
  dcuLiteKey = DEFAULT_ROW_KEYS.dcuLite,
  defaultMode = "grid",
  defaultSort,
  detailColumns,
  drillFilter,
  emaKey = DEFAULT_ROW_KEYS.ema,
  emaOverrideKey = DEFAULT_ROW_KEYS.emaOverride,
  fatColorKey = DEFAULT_ROW_KEYS.fatColor,
  filterControls,
  graderKey = DEFAULT_ROW_KEYS.grader,
  header,
  idKey = DEFAULT_ROW_KEYS.id,
  imgUrlKey = DEFAULT_ROW_KEYS.imgUrl,
  isLoading = false,
  isOpen,
  keyframeHashKey = DEFAULT_ROW_KEYS.keyframeHash,
  killDateKey = DEFAULT_ROW_KEYS.killDate,
  locationKey = DEFAULT_ROW_KEYS.location,
  meatColorKey = DEFAULT_ROW_KEYS.meatColor,
  msaKey = DEFAULT_ROW_KEYS.msa,
  onClose,
  renderItemOverlay,
  onExportCsv,
  rows,
  sexKey = DEFAULT_ROW_KEYS.sex,
  subtitle,
  tileScoreKey,
  tileScoreLabel,
  title,
  usdaMarblingKey = DEFAULT_ROW_KEYS.usdaMarbling,
  useItemDetail,
}: ImagesModalProps) => {
  const { t } = useTranslation("camera");
  const theme = useMantineTheme();

  const keys = useMemo(
    () => ({
      ausMarbling: ausMarblingKey,
      bodyId: bodyIdKey,
      date: dateKey,
      dcuLite: dcuLiteKey,
      ema: emaKey,
      emaOverride: emaOverrideKey,
      fatColor: fatColorKey,
      grader: graderKey,
      id: idKey,
      imgUrl: imgUrlKey,
      keyframeHash: keyframeHashKey,
      killDate: killDateKey,
      location: locationKey,
      meatColor: meatColorKey,
      msa: msaKey,
      sex: sexKey,
      usdaMarbling: usdaMarblingKey,
    }),
    [
      ausMarblingKey,
      bodyIdKey,
      dateKey,
      dcuLiteKey,
      emaKey,
      emaOverrideKey,
      fatColorKey,
      graderKey,
      idKey,
      imgUrlKey,
      keyframeHashKey,
      killDateKey,
      locationKey,
      meatColorKey,
      msaKey,
      sexKey,
      usdaMarblingKey,
    ],
  );

  const [mode, setMode] = useState<ImagesModalMode>(defaultMode);
  const [filters, setFilters] = useState<ImagesFilterState>(EMPTY_FILTER_STATE);
  const [sort, setSort] = useState<ImagesSortState>();
  const [globalFilter, setGlobalFilter] = useState("");
  const [tableRows, setTableRows] = useState<MeqRowData[]>([]);
  const [detailItem, setDetailItem] = useState<LooseObject>();
  const [isDrillActive, setIsDrillActive] = useState(true);

  // The drill-through is the population the reader asked for, so it sits
  // under the popover filters: their option lists are drawn from it, and
  // clearing them never widens past it. Only its own chip does that.
  const drillRows = useMemo(
    () =>
      drillFilter !== undefined && isDrillActive
        ? rows.filter(drillFilter.matches)
        : rows,
    [rows, drillFilter, isDrillActive],
  );
  const filteredRows = useMemo(
    () => applyImageFilters(drillRows, filters, keys),
    [drillRows, filters, keys],
  );
  const gridRows = useMemo(
    () => sortImageRows(filteredRows, sort, keys, columns),
    [filteredRows, sort, keys, columns],
  );
  const tableColumns = useMemo(
    () => withTableSorting(columns, keys, sort),
    [columns, keys, sort],
  );
  const detailRows = mode === "grid" ? gridRows : tableRows;

  // The one sort, in the table engine's shape. The engine speaks column ids
  // and the grid speaks row keys; for every column the modal displays the two
  // resolve to the same string, but the mapping is made explicit rather than
  // assumed.
  const tableSorting = useMemo<MeqSortingState>(() => {
    if (sort === undefined) return [];
    const column = columns.find(
      (entry) => getColumnDataKey(entry) === sort.columnKey,
    );
    const id = column === undefined ? undefined : getColumnId(column);
    return id === undefined ? [] : [{ id, desc: !sort.isAscending }];
  }, [columns, sort]);
  const handleTableSortingChange = useCallback(
    (sorting: MeqSortingState) => {
      const [entry] = sorting;
      if (entry === undefined) {
        setSort(undefined);
        return;
      }
      const column = columns.find(
        (candidate) => getColumnId(candidate) === entry.id,
      );
      const columnKey =
        column === undefined ? entry.id : getColumnDataKey(column);
      setSort(
        columnKey === undefined
          ? undefined
          : { columnKey, isAscending: !entry.desc },
      );
    },
    [columns],
  );

  const seedSort = useMemo(
    () =>
      defaultSort && hasColumn(columns, defaultSort.name)
        ? defaultSort
        : undefined,
    [columns, defaultSort],
  );
  const hasSeededSortRef = useRef(false);

  // Every open is a fresh view of whatever the caller is now showing: the mode
  // it was opened in, no carried-over filters from a different capture day.
  useEffect(() => {
    if (!isOpen) {
      hasSeededSortRef.current = false;
      return;
    }
    setMode(defaultMode);
    setIsDrillActive(true);
    setFilters(EMPTY_FILTER_STATE);
    setGlobalFilter("");
    setDetailItem(undefined);
    setSort(undefined);
  }, [isOpen, defaultMode]);

  // Seeded once per open, and only once the column set has arrived — the query
  // routinely resolves after the modal is already on screen.
  useEffect(() => {
    if (!isOpen || hasSeededSortRef.current || !seedSort) return;
    hasSeededSortRef.current = true;
    setSort({ columnKey: seedSort.name, isAscending: seedSort.isAscending });
  }, [isOpen, seedSort]);

  const handleRowModelChange = useCallback(
    (changedRows: MeqRow<MeqRowData>[]) =>
      setTableRows(changedRows.map((row) => row.original)),
    [],
  );

  const availability = useMemo(
    () => getFilterAvailability(columns, keys),
    [columns, keys],
  );

  // Reports the filters' effect only, never the table's search: it answers "how
  // much of this day is MSA", which a transient search should not restate.
  const resolvedContentTitle = useMemo(() => {
    if (typeof contentTitle !== "function") return contentTitle;
    if (countActiveFilters(filters) === 0) return contentTitle({});

    return contentTitle({
      matched: filteredRows.length,
      filterLabel: getSoleActiveFilterLabel(filters, t),
    });
  }, [contentTitle, filters, filteredRows.length, t]);

  const filtersControl = (
    <FiltersPopover
      availability={availability}
      filterControls={filterControls}
      filters={filters}
      keys={keys}
      onFiltersChange={setFilters}
      rows={drillRows}
    />
  );

  // Heads the content in both modes beside the caller's title, so the reader
  // always sees what narrowed the set and has the one control that widens it.
  const drillChip = drillFilter !== undefined && isDrillActive && (
    <Chip
      data-testid="images-modal-drill-filter"
      label={drillFilter.label}
      tooltipTitle={t("imagesModal.drillFilterRemove")}
      onDelete={() => setIsDrillActive(false)}
    />
  );
  const contentHeading = (
    <Group gap="sm">
      {resolvedContentTitle}
      {drillChip}
    </Group>
  );

  return (
    <DetailDialog
      isOpen={isOpen}
      onClose={onClose}
      icon="Menu"
      title={title}
      subtitle={subtitle}
      closeLabel={t("imagesModal.close")}
      headerActions={
        <>
          {header}
          <Button.Group aria-label={t("imagesModal.viewMode")}>
            {MODES.map(({ icon, labelKey, value }) => (
              <Button
                key={value}
                aria-pressed={mode === value}
                color={theme.primaryColor}
                leftSection={<SvgIconRaw name={icon} size={16} />}
                onClick={() => setMode(value)}
                variant={mode === value ? "filled" : "secondary"}
              >
                {t(labelKey)}
              </Button>
            ))}
          </Button.Group>
        </>
      }
    >
      {mode === "grid" ? (
        // The grid's own card, mirroring the table's: same surface, and a
        // toolbar row carrying the controls MeqTable is handed in table
        // mode (`renderToolbarActions`) so both modes read the same.
        <Paper
          withBorder
          radius="md"
          bg="var(--mantine-color-bg-primary)"
          style={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
            minHeight: 0,
          }}
        >
          <Group justify="space-between" p="sm" wrap="wrap">
            {contentHeading}
            <Group gap="sm">
              {filtersControl}
              <GridSortControl
                columns={columns}
                onSortChange={setSort}
                sort={sort}
              />
            </Group>
          </Group>
          {/* Padding sits here, not on the grid: the virtualiser derives
              its column count from the scroll element's clientWidth,
              which would include the grid's own padding. */}
          <Box
            px="sm"
            pb="sm"
            style={{
              display: "flex",
              flexDirection: "column",
              flex: 1,
              minHeight: 0,
            }}
          >
            {isLoading ? (
              <Spinner />
            ) : gridRows.length === 0 ? (
              <Center flex={1}>
                <Text size="sm" c="dimmed">
                  {t("imagesModal.noImages")}
                </Text>
              </Center>
            ) : (
              <ImageTileGrid
                rows={gridRows}
                bodyIdKey={keys.bodyId}
                idKey={keys.id}
                dateKey={keys.date}
                imgUrlKey={keys.imgUrl}
                keyframeHashKey={keys.keyframeHash}
                locationKey={keys.location}
                scoreKey={tileScoreKey}
                scoreLabel={tileScoreLabel}
                onItemClick={setDetailItem}
                renderItemOverlay={renderItemOverlay}
              />
            )}
          </Box>
        </Paper>
      ) : (
        <MeqTable
          columns={tableColumns}
          data={filteredRows}
          isLoading={isLoading}
          fillHeight
          state={{ globalFilter, sorting: tableSorting }}
          onGlobalFilterChange={setGlobalFilter}
          onSortingChange={handleTableSortingChange}
          onRowClick={(row) => setDetailItem(row.original)}
          onRowModelChange={handleRowModelChange}
          hasBorderedControls
          hasCardSurface
          hasFullscreenToggle={false}
          renderToolbarActions={() => contentHeading}
          renderToolbarActionsRight={() => filtersControl}
          hasExport={onExportCsv !== undefined}
          onExportDataToCsv={() => onExportCsv?.({ rows: tableRows })}
        />
      )}

      {/* Mounted per image rather than kept open and re-fed, so the viewer's
          position within `detailRows` is seeded once from the clicked row. */}
      {detailItem !== undefined && (
        <ImageViewer
          bodyIdKey={keys.bodyId}
          columns={columns}
          dateKey={keys.date}
          detailColumns={detailColumns}
          idKey={keys.id}
          imgUrlKey={keys.imgUrl}
          initialItem={detailItem}
          keyframeHashKey={keys.keyframeHash}
          locationKey={keys.location}
          onClose={() => setDetailItem(undefined)}
          rows={detailRows}
          useItemDetail={useItemDetail}
        />
      )}
    </DetailDialog>
  );
};

export default ImagesModal;
